//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import React, { forwardRef, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ITrackableComponent } from 'unison/dist/src/common/trackable-component';

import { DefaultIconButton } from '@components/atoms/button/Button';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import {
  SUPPORTED_IMAGE_MIME_TYPES,
  SupportedMimeTypes,
} from '@utils/mimeTypes';

interface Props extends React.ComponentProps<'button'>, ITrackableComponent {
  acceptableFiles?: SupportedMimeTypes[];
  className?: string;
  handleChange: (files: FileList | null) => Promise<void>;
  inputId?: string;
  isDisabled: boolean;
  onClick?: () => void;
  shouldAcceptMultipleFiles: boolean;
  buttonText?: string;
}

/**
 * The Upload molecule allows the user to upload a file from their computer.
 * @param handleChange should handle the uploaded file(s)
 * @param inputId id for the input element to use when adding a label
 *
 * @example
 * <label htmlFor='my-input-id'>Label</label>
 * <Upload
 *  inputId='my-input-id'
 *  ...
 * />
 *
 */
export const Upload = forwardRef<HTMLInputElement, Props>(
  (
    {
      acceptableFiles = SUPPORTED_IMAGE_MIME_TYPES,
      buttonText = 'Upload',
      className,
      handleChange,
      inputId,
      isDisabled,
      onClick,
      shouldAcceptMultipleFiles,
      trackingId,
      ...buttonProps
    },
    ref,
  ) => {
    const internalInputRef = useRef<HTMLInputElement>(null);
    const inputRef = ref && typeof ref !== 'function' ? ref : internalInputRef;
    const [_isLoading, setIsLoading] = useState(false);

    const handleButtonClick = () => {
      if (onClick) {
        onClick();
      } else {
        inputRef.current?.click();
      }
    };

    return (
      <Container className={className}>
        <Button
          iconName={_isLoading ? 'spinner' : 'upload'}
          isDisabled={isDisabled || _isLoading}
          onClick={handleButtonClick}
          trackingId={trackingId}
          type='button'
          {...buttonProps}
        >
          {buttonText}
        </Button>
        <InvisibleInput
          accept={acceptableFiles.join(', ')}
          disabled={isDisabled}
          {...(isDefined(inputId) && { id: inputId })}
          ref={inputRef}
          multiple={shouldAcceptMultipleFiles}
          onChange={async (e) => {
            const target = e.currentTarget;
            setIsLoading(true);
            try {
              await handleChange(target.files);
            } finally {
              target.value = '';
              setIsLoading(false);
            }
          }}
          tabIndex={-1}
          type='file'
        />
      </Container>
    );
  },
);

Upload.displayName = 'Upload';

const Container = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const Button = styled(DefaultIconButton)(
  () => css`
    && button {
      &:not([disabled]) {
        &:focus {
          ${Mixins.Focus};
          border-radius: inherit;
        }
      }
    }
  `,
);

const InvisibleInput = styled.input(
  () => css`
    ${Mixins.VisuallyHidden}
  `,
);
